
import React, { useEffect } from "react";
import { Facebook, Twitter, Instagram, Youtube } from "lucide-react";
import Logo from "./Logo";
import { Link } from "react-router-dom";
import { useLanguage } from '@/contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.iubenda.com/iubenda.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <footer className="bg-brand-darkest pt-8 md:pt-16">
      <div className="container mx-auto px-4">
        {/* Mobile Layout - Compact & Centered */}
        <div className="md:hidden flex flex-col items-center text-center pb-6">
          {/* Logo */}
          <div className="mb-4">
            <img
              src="/lovable-uploads/2024-10-logo-pt.png"
              alt="DartsBeat Store"
              className="h-20 mx-auto"
            />
          </div>

          {/* Description */}
          <p className="text-gray-400 text-xs px-6 mb-4 leading-relaxed">
            DARTSBEATSTORE - A loja oficial onde podes trocar os teus storepoints DartsBeat por produtos e serviços exclusivos
          </p>

          {/* Divider */}
          <div className="w-full border-t border-gray-800 mb-4"></div>

          {/* Links Rápidos Section */}
          <div className="mb-4 w-full">
            <h3 className="text-white text-sm font-semibold mb-2">Links Rápidos</h3>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs">
              <Link to="/" className="text-gray-400 hover:text-brand-red transition-colors">{t('footer.home')}</Link>
              <span className="text-gray-700">•</span>
              <Link to="/about-us" className="text-gray-400 hover:text-brand-red transition-colors">{t('header.about')}</Link>
              <span className="text-gray-700">•</span>
              <Link to="/shop" className="text-gray-400 hover:text-brand-red transition-colors">{t('header.products')}</Link>
              <span className="text-gray-700">•</span>
              <Link to="/contact" className="text-gray-400 hover:text-brand-red transition-colors">{t('header.contact')}</Link>
            </div>
          </div>

          {/* Divider */}
          <div className="w-full border-t border-gray-800 mb-4"></div>

          {/* Informações Section */}
          <div className="mb-4 w-full">
            <h3 className="text-white text-sm font-semibold mb-2">Informações</h3>
            <div className="flex flex-wrap justify-center gap-x-3 text-xs text-gray-400">
              <a
                href="https://www.iubenda.com/privacy-policy/91329570"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-brand-red transition-colors"
              >
                {t('footer.privacyPolicy')}
              </a>
              <span className="text-gray-700">•</span>
              <a
                href="https://www.iubenda.com/privacy-policy/91329570/cookie-policy"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-brand-red transition-colors"
              >
                {t('footer.cookiePolicy')}
              </a>
            </div>
          </div>

          {/* Divider */}
          <div className="w-full border-t border-gray-800 mb-4"></div>

          {/* Contacto Section */}
          <div className="mb-4 w-full">
            <h3 className="text-white text-sm font-semibold mb-2">Contacto</h3>
            <div className="space-y-0.5 text-xs text-gray-400">
              <p>Rua D. Antonio Caetano de Sousa, Nº 15</p>
              <p>1500-222 Lisboa, Portugal</p>
              <p className="mt-1">loja@dartsbeatstore.pt</p>
            </div>
          </div>

          {/* Social */}
          <a href="https://www.instagram.com/dartsbeat_portugal/" className="text-gray-400 hover:text-brand-red transition-colors mb-4">
            <Instagram size={20} />
          </a>
        </div>

        {/* Desktop Layout - Original */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8 pb-12">
          <div>
            <div className="mb-4">
              <img
                src="/lovable-uploads/2024-10-logo-pt.png"
                alt="DartsBeat Store"
                className="h-36"
              />
            </div>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/dartsbeat_portugal/" className="text-gray-400 hover:text-brand-red transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-400 hover:text-brand-red transition-colors text-sm">{t('footer.home')}</Link></li>
              <li><Link to="/about-us" className="text-gray-400 hover:text-brand-red transition-colors text-sm">{t('header.about')}</Link></li>
              <li><Link to="/shop" className="text-gray-400 hover:text-brand-red transition-colors text-sm">{t('header.products')}</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-brand-red transition-colors text-sm">{t('header.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.information')}</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.iubenda.com/privacy-policy/91329570"
                  className="iubenda-white iubenda-noiframe iubenda-embed text-gray-400 hover:text-brand-red transition-colors text-sm"
                  title={t('footer.privacyPolicy')}
                >
                  {t('footer.privacyPolicy')}
                </a>
              </li>
              <li>
                <a
                  href="https://www.iubenda.com/privacy-policy/91329570/cookie-policy"
                  className="iubenda-white iubenda-noiframe iubenda-embed text-gray-400 hover:text-brand-red transition-colors text-sm"
                  title={t('footer.cookiePolicy')}
                >
                  {t('footer.cookiePolicy')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-2">
              <li className="text-gray-400 text-sm">Rua D. Antonio Caetano de Sousa, Nº 15 1500-222</li>
              <li className="text-gray-400 text-sm">Lisboa, Portugal</li>
              <li className="text-gray-400 text-sm">loja@dartsbeatstore.pt</li>
              <li className="text-gray-400 text-sm"><a href="https://www.instagram.com/dartsbeat_portugal/" target="_blank" className="hover:text-brand-red transition-colors">Instagram</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 py-4 md:py-6">
          <div className="text-center text-gray-500 text-xs md:text-sm mb-3 md:mb-4">
            © {new Date().getFullYear()} Darts Beat Portugal. {t('footer.allRightsReserved')}.
          </div>

          <div className="flex flex-col items-center justify-center gap-3 md:gap-4">
            <a href="https://playglobal.pt/" target="_blank" rel="noopener noreferrer">
              <img
                loading="lazy"
                decoding="async"
                src="/lovable-uploads/ico-pg.png"
                alt="PlayGlobal"
                className="h-5 md:h-6"
              />
            </a>

            <a href="https://top1.pt/" target="_blank" rel="noopener noreferrer">
              <img
                loading="lazy"
                decoding="async"
                src="/lovable-uploads/ico-top1.png"
                alt="Top1"
                className="h-5 md:h-6"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
